import { PRODUCT_CATEGORY_IDS, type ProductCategoryId } from './constants'
import { normalizeMenuTags, type MenuTags } from './menu-tags'
import { normalizeProductTags, type ProductTags } from './product-tags'
import {
  SAMPLE_CASES_FEATURE,
  type CaseStartInput,
  type SampleCaseIngredient,
} from './schemas/sample-cases'
import { SIMULATION_INPUT_LIMITS, type DisplayCurrency, type Language } from './schemas/simulation'
import { AXES, findMajor, findSub, resolveCategoryId, type Axis } from './taxonomy'

/**
 * 완성 사례 공용 함수 (2단계, 설계 4.4·6.4·6.8). 서버가 시작 입력 글을 만들고, 앱은 스위치 판정만 쓴다.
 * 금액 표기와 마진 계산은 1단계 글 본문(데이터 계약 3.3)과 같은 규칙이다.
 */

/** 시작 입력 메시지 수 상한 (설계 6.4). 생성 입력 한도(30개)보다 훨씬 작게 둔다 */
export const CASE_START_MAX_MESSAGES = 6

/**
 * 앱이 진입점(홈 줄, 시작 화면 카드, 글 상세 버튼)을 그릴지 (설계 6.8). 서버 코드에 기능이 있고(features.sampleCases)
 * 관리자 설정에서 켰을 때(settings.sampleCases.enabled)만 참. 옛 서버·오프라인 기본값이면 거짓
 */
export function sampleCasesVisible(
  config:
    | {
        features?: { [SAMPLE_CASES_FEATURE]?: unknown } | null
        settings?: { [SAMPLE_CASES_FEATURE]?: { enabled?: unknown } | null } | null
      }
    | null
    | undefined,
): boolean {
  return (
    config?.features?.[SAMPLE_CASES_FEATURE] === true &&
    config?.settings?.[SAMPLE_CASES_FEATURE]?.enabled === true
  )
}

/** 샘플번호 앞 2자 = 나라 (KR-001 은 KR) */
export function caseCountryOf(sampleNo: string): string {
  return sampleNo.slice(0, 2).toUpperCase()
}

/**
 * 나라 이름은 코드 안의 고정 표 (Hermes 에서 Intl 나라 이름을 믿을 수 없다, 설계 17장). 앱과 관리자(ko)가 같이 쓴다.
 * 표에 없는 코드는 코드 그대로 보인다 (설계 4.2)
 */
export const CASE_COUNTRY_LABELS: Record<string, Record<Language, string>> = {
  KR: { ko: '한국', en: 'Korea', ja: '韓国', zh: '韩国' },
  US: { ko: '미국', en: 'USA', ja: 'アメリカ', zh: '美国' },
  JP: { ko: '일본', en: 'Japan', ja: '日本', zh: '日本' },
  CN: { ko: '중국', en: 'China', ja: '中国', zh: '中国' },
  TW: { ko: '대만', en: 'Taiwan', ja: '台湾', zh: '台湾' },
  HK: { ko: '홍콩', en: 'Hong Kong', ja: '香港', zh: '香港' },
  VN: { ko: '베트남', en: 'Vietnam', ja: 'ベトナム', zh: '越南' },
  TH: { ko: '태국', en: 'Thailand', ja: 'タイ', zh: '泰国' },
  ID: { ko: '인도네시아', en: 'Indonesia', ja: 'インドネシア', zh: '印度尼西亚' },
  PH: { ko: '필리핀', en: 'Philippines', ja: 'フィリピン', zh: '菲律宾' },
  MY: { ko: '말레이시아', en: 'Malaysia', ja: 'マレーシア', zh: '马来西亚' },
  SG: { ko: '싱가포르', en: 'Singapore', ja: 'シンガポール', zh: '新加坡' },
  IN: { ko: '인도', en: 'India', ja: 'インド', zh: '印度' },
  GB: { ko: '영국', en: 'UK', ja: 'イギリス', zh: '英国' },
  FR: { ko: '프랑스', en: 'France', ja: 'フランス', zh: '法国' },
  DE: { ko: '독일', en: 'Germany', ja: 'ドイツ', zh: '德国' },
  IT: { ko: '이탈리아', en: 'Italy', ja: 'イタリア', zh: '意大利' },
  ES: { ko: '스페인', en: 'Spain', ja: 'スペイン', zh: '西班牙' },
  CA: { ko: '캐나다', en: 'Canada', ja: 'カナダ', zh: '加拿大' },
  AU: { ko: '호주', en: 'Australia', ja: 'オーストラリア', zh: '澳大利亚' },
  MX: { ko: '멕시코', en: 'Mexico', ja: 'メキシコ', zh: '墨西哥' },
  BR: { ko: '브라질', en: 'Brazil', ja: 'ブラジル', zh: '巴西' },
  // 국가별 요리 템플릿(2026-11-01)에 쓰는 나라. 옛 앱은 이 코드를 코드 그대로 보이지만 템플릿 자체를 받지 않는다
  GR: { ko: '그리스', en: 'Greece', ja: 'ギリシャ', zh: '希腊' },
  TR: { ko: '튀르키예', en: 'Türkiye', ja: 'トルコ', zh: '土耳其' },
  MA: { ko: '모로코', en: 'Morocco', ja: 'モロッコ', zh: '摩洛哥' },
  PE: { ko: '페루', en: 'Peru', ja: 'ペルー', zh: '秘鲁' },
  AR: { ko: '아르헨티나', en: 'Argentina', ja: 'アルゼンチン', zh: '阿根廷' },
  CL: { ko: '칠레', en: 'Chile', ja: 'チリ', zh: '智利' },
  CO: { ko: '콜롬비아', en: 'Colombia', ja: 'コロンビア', zh: '哥伦比亚' },
  SE: { ko: '스웨덴', en: 'Sweden', ja: 'スウェーデン', zh: '瑞典' },
  DK: { ko: '덴마크', en: 'Denmark', ja: 'デンマーク', zh: '丹麦' },
  NO: { ko: '노르웨이', en: 'Norway', ja: 'ノルウェー', zh: '挪威' },
  FI: { ko: '핀란드', en: 'Finland', ja: 'フィンランド', zh: '芬兰' },
  PL: { ko: '폴란드', en: 'Poland', ja: 'ポーランド', zh: '波兰' },
  RU: { ko: '러시아', en: 'Russia', ja: 'ロシア', zh: '俄罗斯' },
  CH: { ko: '스위스', en: 'Switzerland', ja: 'スイス', zh: '瑞士' },
  AT: { ko: '오스트리아', en: 'Austria', ja: 'オーストリア', zh: '奥地利' },
  NL: { ko: '네덜란드', en: 'Netherlands', ja: 'オランダ', zh: '荷兰' },
  BE: { ko: '벨기에', en: 'Belgium', ja: 'ベルギー', zh: '比利时' },
  PT: { ko: '포르투갈', en: 'Portugal', ja: 'ポルトガル', zh: '葡萄牙' },
  IE: { ko: '아일랜드', en: 'Ireland', ja: 'アイルランド', zh: '爱尔兰' },
  LB: { ko: '레바논', en: 'Lebanon', ja: 'レバノン', zh: '黎巴嫩' },
  EG: { ko: '이집트', en: 'Egypt', ja: 'エジプト', zh: '埃及' },
  ZA: { ko: '남아프리카공화국', en: 'South Africa', ja: '南アフリカ', zh: '南非' },
  NZ: { ko: '뉴질랜드', en: 'New Zealand', ja: 'ニュージーランド', zh: '新西兰' },
  ET: { ko: '에티오피아', en: 'Ethiopia', ja: 'エチオピア', zh: '埃塞俄比亚' },
  NG: { ko: '나이지리아', en: 'Nigeria', ja: 'ナイジェリア', zh: '尼日利亚' },
  GH: { ko: '가나', en: 'Ghana', ja: 'ガーナ', zh: '加纳' },
  KE: { ko: '케냐', en: 'Kenya', ja: 'ケニア', zh: '肯尼亚' },
  JM: { ko: '자메이카', en: 'Jamaica', ja: 'ジャマイカ', zh: '牙买加' },
  HU: { ko: '헝가리', en: 'Hungary', ja: 'ハンガリー', zh: '匈牙利' },
  KH: { ko: '캄보디아', en: 'Cambodia', ja: 'カンボジア', zh: '柬埔寨' },
  MM: { ko: '미얀마', en: 'Myanmar', ja: 'ミャンマー', zh: '缅甸' },
  // 템플릿 v2 (602개, 2026-11-01)에 새로 생긴 나라. 나라 이름만 중립적인 표준 표기로 쓴다
  AO: { ko: '앙골라', en: 'Angola', ja: 'アンゴラ', zh: '安哥拉' },
  CG: { ko: '콩고 공화국', en: 'Republic of the Congo', ja: 'コンゴ共和国', zh: '刚果共和国' },
  CI: { ko: '코트디부아르', en: 'Côte d’Ivoire', ja: 'コートジボワール', zh: '科特迪瓦' },
  ER: { ko: '에리트레아', en: 'Eritrea', ja: 'エリトリア', zh: '厄立特里亚' },
  GA: { ko: '가봉', en: 'Gabon', ja: 'ガボン', zh: '加蓬' },
  IL: { ko: '이스라엘', en: 'Israel', ja: 'イスラエル', zh: '以色列' },
  JO: { ko: '요르단', en: 'Jordan', ja: 'ヨルダン', zh: '约旦' },
  LA: { ko: '라오스', en: 'Laos', ja: 'ラオス', zh: '老挝' },
  LK: { ko: '스리랑카', en: 'Sri Lanka', ja: 'スリランカ', zh: '斯里兰卡' },
  LR: { ko: '라이베리아', en: 'Liberia', ja: 'リベリア', zh: '利比里亚' },
  MG: { ko: '마다가스카르', en: 'Madagascar', ja: 'マダガスカル', zh: '马达加斯加' },
  ML: { ko: '말리', en: 'Mali', ja: 'マリ', zh: '马里' },
  NP: { ko: '네팔', en: 'Nepal', ja: 'ネパール', zh: '尼泊尔' },
  PK: { ko: '파키스탄', en: 'Pakistan', ja: 'パキスタン', zh: '巴基斯坦' },
  SL: { ko: '시에라리온', en: 'Sierra Leone', ja: 'シエラレオネ', zh: '塞拉利昂' },
  SN: { ko: '세네갈', en: 'Senegal', ja: 'セネガル', zh: '塞内加尔' },
  SO: { ko: '소말리아', en: 'Somalia', ja: 'ソマリア', zh: '索马里' },
  TD: { ko: '차드', en: 'Chad', ja: 'チャド', zh: '乍得' },
  TG: { ko: '토고', en: 'Togo', ja: 'トーゴ', zh: '多哥' },
  UG: { ko: '우간다', en: 'Uganda', ja: 'ウガンダ', zh: '乌干达' },
  ZM: { ko: '잠비아', en: 'Zambia', ja: 'ザンビア', zh: '赞比亚' },
  ZW: { ko: '짐바브웨', en: 'Zimbabwe', ja: 'ジンバブエ', zh: '津巴布韦' },
  // 템플릿 v3 (2026-11-02): 샥슈카의 나라 (대표 결정, 원조 논쟁은 튀니지로)
  TN: { ko: '튀니지', en: 'Tunisia', ja: 'チュニジア', zh: '突尼斯' },
}

export function caseCountryLabel(code: string, lang: Language): string {
  return CASE_COUNTRY_LABELS[code]?.[lang] ?? code
}

/** 마진(%) = (P - C) / P x 100 을 JS Math.round 로. 판매가가 0 이하이면 0 */
export function caseMarginPct(retail: number, unitCost: number): number {
  if (!(retail > 0)) return 0
  // Math.round(-0.4) 는 -0 이라 0 으로 맞춘다
  return Math.round(((retail - unitCost) / retail) * 100) || 0
}

function groupThousands(intPart: string): string {
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/** 금액 표기 (데이터 계약 3.3 의 표). 원·엔은 정수, 달러·위안은 소수 두 자리, 천 단위 쉼표 */
export function formatCaseMoney(
  value: number,
  currency: DisplayCurrency,
  language: Language,
): string {
  const d = currency === 'KRW' || currency === 'JPY' ? 0 : 2
  const f = 10 ** d
  const rounded = Math.round(value * f + (value >= 0 ? 1e-9 : -1e-9)) / f
  const [i, frac] = Math.abs(rounded).toFixed(d).split('.')
  const n = `${rounded < 0 ? '-' : ''}${groupThousands(i ?? '0')}${frac ? `.${frac}` : ''}`
  switch (currency) {
    case 'KRW':
      return language === 'ko' ? `${n}원` : `₩${n}`
    case 'USD':
      return `$${n}`
    case 'JPY':
      if (language === 'ko') return `${n}엔`
      if (language === 'en') return `¥${n}`
      return language === 'ja' ? `${n}円` : `${n}日元`
    case 'CNY':
      if (language === 'ko') return `${n}위안`
      if (language === 'en') return `CN¥${n}`
      return language === 'ja' ? `${n}元` : `¥${n}`
  }
}

/** 양 표기: 뒤의 0 을 지운다 (120.0 은 120). 지수 표기는 쓰지 않는다 */
export function formatCaseQty(qty: number): string {
  const s = String(qty)
  if (!/e/i.test(s)) return s
  return qty.toFixed(6).replace(/0+$/, '').replace(/\.$/, '')
}

/** 사례 분류가 지금 코드의 분류표에 있는지. 없으면 시작 입력을 만들지 않는다 (설계 6.2) */
export function caseTaxonomyValid(c: {
  axis: string
  majorId: string
  subId: string | null
  categoryId: string
}): c is { axis: Axis; majorId: string; subId: string | null; categoryId: ProductCategoryId } {
  if (!(AXES as readonly string[]).includes(c.axis)) return false
  const axis = c.axis as Axis
  if (!findMajor(axis, c.majorId)) return false
  if (c.subId && !findSub(axis, c.majorId, c.subId)) return false
  if (!(PRODUCT_CATEGORY_IDS as readonly string[]).includes(c.categoryId)) return false
  return resolveCategoryId(axis, c.majorId, c.subId) === c.categoryId
}

/** 시작 입력 글의 라벨 (설계 6.4 의 표) */
interface StartWords {
  intro: string
  product: string
  about: string
  format: string
  retail: string
  unitCost: string
  ingredientsPublic: (b: number) => string
  ingredientsPrivate: string
  steps: string
  /** 라벨과 값 사이 */
  colon: string
  /** 이름만 늘어놓을 때 */
  sep: string
}

const START_WORDS: Record<Language, StartWords> = {
  ko: {
    intro: '이 완성 사례를 바탕으로 내 제품을 만들어 주세요.',
    product: '제품',
    about: '소개',
    format: '판매 단위',
    retail: '판매가',
    unitCost: '1개 원가',
    ingredientsPublic: (b) => `재료 (${b}개 분량)`,
    ingredientsPrivate: '재료 (양 비공개)',
    steps: '만드는 법',
    colon: ': ',
    sep: ', ',
  },
  en: {
    intro: 'Please build my product from this finished example.',
    product: 'Product',
    about: 'About',
    format: 'Sold as',
    retail: 'Sells for',
    unitCost: 'Cost per unit',
    ingredientsPublic: (b) => `Ingredients (makes ${b})`,
    ingredientsPrivate: 'Ingredients (amounts not shared)',
    steps: 'How it’s made',
    colon: ': ',
    sep: ', ',
  },
  ja: {
    intro: 'この完成事例をもとに私の商品を作ってください。',
    product: '商品',
    about: '紹介',
    format: '販売単位',
    retail: '販売価格',
    unitCost: '1個あたり原価',
    ingredientsPublic: (b) => `材料（${b}個分）`,
    ingredientsPrivate: '材料（分量は非公開）',
    steps: '作り方',
    colon: '：',
    sep: '、',
  },
  zh: {
    intro: '请以这个成品案例为基础制作我的产品。',
    product: '产品',
    about: '简介',
    format: '规格',
    retail: '售价',
    unitCost: '单件成本',
    ingredientsPublic: (b) => `食材（${b}件用量）`,
    ingredientsPrivate: '食材（用量未公开）',
    steps: '制作方法',
    colon: '：',
    sep: '、',
  },
}

/** 시작 입력 글을 만드는 데 쓰는 사례 값 */
export interface CaseStartSource {
  language: Language
  name: string
  tagline: string
  format: string
  sourceCurrency: DisplayCurrency
  sourceRetail: number
  sourceUnitCost: number
  batchUnits: number
  recipePublic: boolean
  ingredients: readonly SampleCaseIngredient[]
  steps: readonly string[]
}

/**
 * 줄 묶음을 메시지 한도(4,000자) 안으로 나눈다. 줄 단위로 채우고, 한 줄이 한도보다 길면 그 줄만 글자로 자른다.
 * 글자 수는 서버 검사(zod max)와 같이 UTF-16 길이로 센다
 */
function packLines(lines: readonly string[], max: number): string[] {
  const out: string[] = []
  let cur = ''
  const flush = () => {
    if (cur) out.push(cur)
    cur = ''
  }
  for (const raw of lines) {
    let line = raw
    while (line.length > max) {
      flush()
      out.push(line.slice(0, max))
      line = line.slice(max)
    }
    const next = cur ? `${cur}\n${line}` : line
    if (next.length > max) {
      flush()
      cur = line
    } else {
      cur = next
    }
  }
  flush()
  return out
}

/**
 * '이 사례로 시작하기' 입력 메시지 (설계 6.4). 사례 언어로 쓴다. 결과는 분석 모드가 앱 언어로 옮긴다.
 * - 메시지 1: 첫 줄, 제품, 소개, 판매 단위, 판매가, 1개 원가 (엑셀 통화)
 * - 메시지 2: 재료. 양 공개면 머리줄(재료 기준 개수)과 '- 이름 양 단위' 줄, 비공개면 이름만 한 줄
 * - 메시지 3: 만드는 법 '1. 설명' 줄
 * 메시지마다 4,000자 이하, 모두 6개 이하. 사진은 넣지 않는다 (생성 입력은 sim-uploads 키만 받는다)
 */
export function composeCaseStartMessages(source: CaseStartSource): { text: string }[] {
  const w = START_WORDS[source.language] ?? START_WORDS.en
  const max = SIMULATION_INPUT_LIMITS.textLength
  const money = (v: number) => formatCaseMoney(v, source.sourceCurrency, source.language)
  const label = (k: string, v: string) => `${k}${w.colon}${v}`

  const head = [
    w.intro,
    label(w.product, source.name),
    ...(source.tagline.trim() ? [label(w.about, source.tagline)] : []),
    label(w.format, source.format),
    label(w.retail, money(source.sourceRetail)),
    label(w.unitCost, money(source.sourceUnitCost)),
  ]

  const ingredients = [...source.ingredients].sort((a, b) => a.order - b.order)
  let ingredientLines: string[] = []
  if (ingredients.length > 0) {
    if (source.recipePublic) {
      ingredientLines = [
        w.ingredientsPublic(source.batchUnits),
        ...ingredients.map((i) =>
          i.qty !== null
            ? `- ${i.name} ${formatCaseQty(i.qty)}${i.unit ? ` ${i.unit}` : ''}`
            : `- ${i.name}`,
        ),
      ]
    } else {
      ingredientLines = [
        `${w.ingredientsPrivate}${w.colon}${ingredients.map((i) => i.name).join(w.sep)}`,
      ]
    }
  }

  const stepLines =
    source.steps.length > 0 ? [w.steps, ...source.steps.map((s, i) => `${i + 1}. ${s}`)] : []

  const messages = [head, ingredientLines, stepLines]
    .filter((lines) => lines.length > 0)
    .flatMap((lines) => packLines(lines, max))
    .slice(0, CASE_START_MAX_MESSAGES)
  return messages.map((text) => ({ text }))
}

/* ── 템플릿 시작 (국가별 요리 템플릿, 2026-11-01) ───────────────────────────── */

/** 템플릿 시작 글의 라벨. 사용자가 설명 화면에서 보내는 첫 말풍선이 되므로 사용자 말투로 쓴다 */
const TEMPLATE_WORDS: Record<
  Language,
  { head: string; ingredients: string; colon: string; sep: string; paren: (s: string) => string }
> = {
  ko: {
    head: '템플릿에서 시작',
    ingredients: '주요 재료',
    colon: ': ',
    sep: ', ',
    paren: (s) => ` (${s})`,
  },
  en: {
    head: 'Starting from a template',
    ingredients: 'Key ingredients',
    colon: ': ',
    sep: ', ',
    paren: (s) => ` (${s})`,
  },
  ja: {
    head: 'テンプレートから開始',
    ingredients: '主な材料',
    colon: '：',
    sep: '、',
    paren: (s) => `（${s}）`,
  },
  zh: {
    head: '从模板开始',
    ingredients: '主要食材',
    colon: '：',
    sep: '、',
    paren: (s) => `（${s}）`,
  },
}

/** 템플릿 시작 글을 만드는 데 쓰는 값 */
export interface TemplateStartSource {
  language: Language
  name: string
  tagline: string
  /** 나라 코드 (샘플번호 앞 2자). 글에 나라 이름으로 들어간다 */
  country: string
  ingredients: readonly Pick<SampleCaseIngredient, 'order' | 'name'>[]
}

/**
 * 템플릿 시작 메시지 하나 (사례 언어). '템플릿에서 시작: 이름 (나라)', 소개, 주요 재료 이름만.
 * 양·단가·만드는 법은 넣지 않는다 — 템플릿은 출발점이고 레시피는 생성이 새로 쓴다. 4,000자 안에서 자른다
 */
export function composeTemplateStartMessages(source: TemplateStartSource): { text: string }[] {
  const w = TEMPLATE_WORDS[source.language] ?? TEMPLATE_WORDS.en
  const names = [...source.ingredients].sort((a, b) => a.order - b.order).map((i) => i.name)
  const lines = [
    `${w.head}${w.colon}${source.name}${w.paren(caseCountryLabel(source.country, source.language))}`,
    ...(source.tagline.trim() ? [source.tagline.trim()] : []),
    ...(names.length > 0 ? [`${w.ingredients}${w.colon}${names.join(w.sep)}`] : []),
  ]
  return packLines(lines, SIMULATION_INPUT_LIMITS.textLength)
    .slice(0, 1)
    .map((text) => ({ text }))
}

/** 템플릿 꼬리표를 축에 맞게 정리한다. 메뉴·코스 축은 메뉴 꼬리표, 식품 축은 식품 꼬리표만 남는다 */
export function templateTagsFor(
  axis: Axis,
  tags: unknown,
): { menuTags: MenuTags; productTags: ProductTags } {
  const raw = tags && typeof tags === 'object' && !Array.isArray(tags) ? tags : {}
  return {
    menuTags: normalizeMenuTags(raw as MenuTags, axis),
    productTags: normalizeProductTags(raw as ProductTags, axis),
  }
}

/**
 * 템플릿 상세의 startInput. mode create 로 분류·꼬리표·짧은 소개만 채운다 (생성 호출이 아니다).
 * 분류가 지금 분류표에 없으면 null. 사례·설정의 시작 허용은 부르는 쪽이 본다
 */
export function buildTemplateStartInput(
  c: TemplateStartSource & {
    axis: string
    majorId: string
    subId: string | null
    categoryId: string
    tags: unknown
  },
): CaseStartInput | null {
  if (!caseTaxonomyValid(c)) return null
  const { menuTags, productTags } = templateTagsFor(c.axis, c.tags)
  return {
    mode: 'create',
    axis: c.axis,
    majorId: c.majorId,
    subId: c.subId,
    categoryId: c.categoryId,
    messages: composeTemplateStartMessages(c),
    ...(Object.keys(menuTags).length > 0 ? { menuTags } : {}),
    ...(Object.keys(productTags).length > 0 ? { productTags } : {}),
  }
}

/**
 * 상세 응답의 startInput (설계 6.2). 분류가 지금 분류표에 없으면 null. 사례·설정의 시작 허용은 부르는 쪽이 본다
 */
export function buildCaseStartInput(
  c: CaseStartSource & { axis: string; majorId: string; subId: string | null; categoryId: string },
): CaseStartInput | null {
  if (!caseTaxonomyValid(c)) return null
  return {
    mode: 'analyze',
    axis: c.axis,
    majorId: c.majorId,
    subId: c.subId,
    categoryId: c.categoryId,
    messages: composeCaseStartMessages(c),
  }
}
